import React, { useContext, useEffect, useState } from "react";
import processingGif from "../../assets/auth/onboard/bubble-gum-downloading.gif";
import OnboardContext from "./context";
import AppContext from "../../AppContext";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";
import useAuth from "../../hooks/useAuth";
import useCertificates from "../../hooks/useCertificates";
import { toast } from "react-toastify";
import { addDays, addMonths, startOfDay } from "date-fns";
import _ from "lodash";

export const Processing = () => {
  const { navigate, information, selectedPlan, currentPlan } =
    useContext(OnboardContext);
  const { user, setToken, refetchUser } = useContext(AppContext);
  const {
    createUser,
    uploadAvatar,
    createCompany,
    patchUser,
    uploadCompanyLogo,
    queryRoles,
    transferWorkToCompany,
    transferCertificateToCompany,
  } = useUsers();
  const { uploadCompanyDBD } = useCompanies();
  const { signIn, signInExternal } = useAuth();
  const { createCertificate, uploadCertificate } = useCertificates();

  useEffect(() => {
    if (currentPlan) {
      update();
      return;
    }

    create();
  }, []);

  const create = async () => {
    // * Create user
    const {
      credentials: { username, password, lineId, googleId, facebookId },
      info: {
        firstname,
        lastname,
        phoneNo,
        day,
        month,
        year,
        company,
        taxId,
        experience,
      },
      address,
      avatar,
      companyLogo,
      registrationId,
      interestWork,
      interestStyle,
      interestSpaces,
    } = information;

    const blobAvatar = typeof avatar === "string" ? avatar : undefined;
    const fileAvatar = typeof avatar === "object" ? avatar : undefined;
    const student = _getStudentData();

    let _trialEndOn = undefined;

    if (
      selectedPlan.name === "Freelancer" ||
      selectedPlan.name === "Business"
    ) {
      _trialEndOn = startOfDay(addMonths(new Date(), 3));
    }

    const body = {
      email: username,
      password: password,
      lineId: lineId,
      googleId: googleId,
      facebookId: facebookId,
      avatar: blobAvatar,
      firstname: firstname,
      surname: lastname,
      dateOfBirth: new Date(+year, +month - 1, +day),
      roles: [],
      plan: selectedPlan._id,
      experience: experience ?? 1,
      status: ["Guest", "Trial"].includes(selectedPlan.name) ? 2 : 1,
      student,
      contact: {
        phone: phoneNo,
        address: address,
      },
      interests: {
        works: interestWork,
        spaces: interestSpaces,
        styles: interestStyle,
      },
      taxId,
      trialEndOn: _trialEndOn,
    };

    const { data: user } = await createUser(body, registrationId);

    // * Upload Avatar
    if (!fileAvatar) {
      try {
        const res = await fetch(blobAvatar);

        if (res.ok) {
          const _blob = await res.blob();

          await uploadAvatar(
            user._id,
            new File([_blob], "avatar.png", { type: "image/png" }),
            registrationId
          );
        }
      } catch (error) {}
    }

    if (fileAvatar) {
      await uploadAvatar(user._id, fileAvatar, registrationId);
    }

    // * Remove localStorage used in onboard
    let externalId = "";

    if (registrationId === "line" && lineId) {
      externalId = lineId;
    }

    if (registrationId === "google" && googleId) {
      externalId = googleId;
    }

    if (registrationId === "facebook" && facebookId) {
      externalId = facebookId;
    }

    if (externalId) {
      const token = await signInExternal(externalId, registrationId);
      setToken(token);
    } else {
      const token = await signIn(username, password);
      setToken(token);
    }

    // * Create company
    if (company) {
      const company = await saveCompany(user._id, companyLogo);

      // * Create certificate
      await saveCertificate(user._id, company?._id);
      // * Create DBD
      await saveDBD(company?._id);
    }

    localStorage.removeItem("onboard:credentials");
    localStorage.removeItem("onboard:registration-id");
    navigate("/");
  };

  /**
   * Update a user and/or create a new company
   */
  const update = async () => {
    const {
      info: { phoneNo, day, month, year, company, taxId, experience },
      address,
      companyLogo,
      payment,
    } = information;

    const body = {
      dateOfBirth: new Date(+year, +month - 1, +day),
      roles: [],
      plan: selectedPlan._id,
      experience: experience ?? 1,
      status: ["Guest", "Trial"].includes(selectedPlan.name) ? 2 : 1,
      contact: {
        phone: phoneNo,
        address: address,
      },
      taxId,
      company: null,
      paymentMethod: {
        cardId: payment,
      },
    };

    if (!payment) delete body.paymentMethod;

    await patchUser(user._id, body);

    // * Create company
    if (company) {
      await saveCompany(user._id, companyLogo);
    }

    // const _token = _.cloneDeep(token);

    await refetchUser();

    navigate("/profile/edit");
  };

  const _getStudentData = () => {
    if (selectedPlan.name !== "Student") return {};

    const {
      info: { studentId, university, startingYear, endingYear, major, degree },
    } = information;

    return {
      code: studentId,
      university: university,
      major: major,
      degree: degree,
      startingYear: startingYear,
      endingYear: endingYear,
    };
  };

  const saveCertificate = async (userId, companyId) => {
    try {
      const { file, name, issuer, month, year, url } =
        information.certificate || {};
      const { company } = information.info || {};

      if (!name) return;
      const body = {
        name,
        issuer,
        effectiveDate: new Date(year, +month - 1, 1),
        url,
        user: !company ? userId : undefined,
        company: company ? companyId : undefined,
        status: 1,
      };

      const certificateResult = await createCertificate(body);
      if (file) {
        await uploadCertificate(certificateResult._id, file);
      }
      return certificateResult;
    } catch (error) {
      toast.error("Unable to create certificate");
      return;
    }
  };

  const saveDBD = async (companyId) => {
    try {
      const { dbd } = information;
      if (!dbd || !companyId) return;
      await uploadCompanyDBD(companyId, dbd);
    } catch (error) {
      toast.error("Unable to upload dbd");
      return;
    }
  };

  const saveCompany = async (userId, companyLogo) => {
    try {
      const {
        info: { company, phoneNo, experience, services },
        address,
      } = information;
      if (!company) return undefined;

      // * Get Owner role
      const {
        data: [_ownerRole],
      } = await queryRoles({ name: "Owner" }, 1, 1);

      const body = {
        name: company,
        experience: experience,
        service: services,
        phone: phoneNo.toString(),
        address: { ...address, postalCode: address.postalCode.toString() },
        enterprise: false,
        status: 1,
      };

      const companyResult = await createCompany(body);

      // * Move works to company
      await transferWorkToCompany(userId, companyResult._id);

      // * Move works to company
      await transferCertificateToCompany(userId, companyResult._id);

      // * Patch company to user
      await patchUser(userId, {
        company: companyResult._id,
        roles: [_ownerRole._id],
      });

      // * === Upload logo ===

      // * Upload right away if already a File
      if (typeof companyLogo === "object") {
        await uploadCompanyLogo(companyResult._id, companyLogo);
        return companyResult;
      }

      // * Convert to file if blob
      // Decode the Base64 string to binary data
      const byteString = atob(companyLogo.split(",")[1]);
      const byteArray = new Uint8Array(byteString.length);

      // Convert each character to its UTF-16 code unit
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }

      await uploadCompanyLogo(
        companyResult._id,
        new File([byteArray], "logo.png", { type: "image/png" })
      );

      return companyResult;
    } catch (error) {
      console.error(error);

      toast.error("Unable to create company");
      return undefined;
    }
  };

  return (
    <div className="onboard-processing">
      <img
        className="onboard-processing__image"
        src={processingGif}
        alt="processing"
      />
      <span className="onboard-processing__desc">
        {!currentPlan && "Creating your profile, please hang tight..."}
        {currentPlan && "Updating your profile, please hang tight..."}
      </span>
      {/* {isShowMigrateModal && _getMigrateModal()} */}
    </div>
  );
};
